import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  LogOut,
  ChevronLeft,
  ChevronRight,
  User,
  Users,
  PlusCircle,
  List,
  ShieldCheck,
  Menu,
  X,
} from 'lucide-react';
import { logout, getUser } from '../../utils/auth';
import { toast } from 'react-toastify';
import ProfileModal from './ProfileModal';

const menuItems = [
  { path: '/admin',              icon: LayoutDashboard, label: 'Dashboard',      exact: true },
  { path: '/admin/users',        icon: Users,           label: 'User Management' },
  { path: '/admin/blogs/create', icon: PlusCircle,      label: 'Create Blog' },
  { path: '/admin/blogs',        icon: List,            label: 'Manage Blogs' },
];

const AdminLayout = ({ children }) => {
  const navigate  = useNavigate();
  const location  = useLocation();

  const [collapsed,    setCollapsed]    = useState(false);
  const [mobileOpen,   setMobileOpen]   = useState(false);
  const [profileOpen,  setProfileOpen]  = useState(false);
  const [currentUser,  setCurrentUser]  = useState(getUser());

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
  });

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const handleProfileUpdate = (updatedUser) => {
    setCurrentUser(updatedUser);
  };

  const isActive = (path, exact = false) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  /* ── Sidebar nav link ── */
  const SidebarLink = ({ item, mobile = false }) => {
    const Icon   = item.icon;
    const active = isActive(item.path, item.exact);
    return (
      <Link
        to={item.path}
        onClick={() => mobile && setMobileOpen(false)}
        title={!mobile && collapsed ? item.label : undefined}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 group relative ${
          active ? 'text-white' : 'hover:bg-white/5'
        }`}
        style={active ? { backgroundColor: '#FF9B51', color: '#fff' } : { color: '#BFC9D1' }}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        {(!collapsed || mobile) && (
          <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
        )}
        {/* Collapsed tooltip */}
        {collapsed && !mobile && (
          <span className="absolute left-full ml-3 px-2 py-1 bg-white text-xs font-medium rounded-lg shadow-brand opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50"
            style={{ color: '#25343F' }}>
            {item.label}
          </span>
        )}
      </Link>
    );
  };

  /* ── Clickable user badge ── */
  const UserBadge = ({ compact = false }) => (
    <button
      onClick={() => setProfileOpen(true)}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all hover:bg-white/10 text-left ${
        compact ? 'justify-center' : ''
      }`}
      title={compact ? 'My Profile' : undefined}
    >
      <div
        className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center font-bold text-sm text-white"
        style={{ background: 'linear-gradient(135deg,#FF9B51,#e07a33)' }}
      >
        {currentUser?.name
          ? currentUser.name[0].toUpperCase()
          : <User className="w-4 h-4" />}
      </div>
      {!compact && (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate">
            {currentUser?.name || currentUser?.username}
          </p>
          <p className="text-xs capitalize" style={{ color: '#BFC9D1' }}>{currentUser?.role}</p>
        </div>
      )}
    </button>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EAEFEF', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* ── Desktop Sidebar ── */}
      <aside
        className={`hidden md:flex flex-col fixed left-0 top-0 h-full z-40 transition-all duration-300 ease-in-out shadow-brand ${
          collapsed ? 'w-[72px]' : 'w-64'
        }`}
        style={{ backgroundColor: '#25343F' }}
      >
        {/* Logo row */}
        <div className="flex items-center justify-between px-4 h-16 border-b flex-shrink-0"
          style={{ borderColor: 'rgba(191,201,209,0.12)' }}>
          {collapsed ? (
            <div className="mx-auto w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FF9B51' }}>
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FF9B51' }}>
                  <ShieldCheck className="w-4 h-4 text-white" />
                </div>
                <span className="text-white font-bold text-base">B&amp;B Co.</span>
              </div>
              <button
                onClick={() => setCollapsed(true)}
                className="p-1 rounded-lg transition-colors hover:bg-white/10"
                style={{ color: '#BFC9D1' }}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {/* Expand toggle when collapsed */}
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            className="flex justify-center mt-3 mx-2 p-2 rounded-xl hover:bg-white/10 transition-colors"
            style={{ color: '#BFC9D1' }}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        {/* Nav */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {menuItems.map(item => <SidebarLink key={item.path} item={item} />)}
        </nav>

        {/* User badge + logout */}
        <div className="px-2 pb-4 border-t pt-3 flex-shrink-0 space-y-1"
          style={{ borderColor: 'rgba(191,201,209,0.12)' }}>
          <UserBadge compact={collapsed} />
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 text-red-400 hover:bg-red-500/10 hover:text-red-300 ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* ── Mobile top bar ── */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 flex items-center justify-between px-4 shadow"
        style={{ backgroundColor: '#25343F' }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FF9B51' }}>
            <ShieldCheck className="w-4 h-4 text-white" />
          </div>
          <span className="text-white font-bold">B&amp;B Co.</span>
        </div>
        <button onClick={() => setMobileOpen(!mobileOpen)} className="text-white p-1.5 rounded-lg hover:bg-white/10">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* ── Mobile drawer ── */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-30 mt-14" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="absolute left-0 top-0 bottom-0 w-64 flex flex-col py-4 px-2"
            style={{ backgroundColor: '#25343F' }}
            onClick={e => e.stopPropagation()}
          >
            <nav className="flex-1 space-y-1">
              {menuItems.map(item => <SidebarLink key={item.path} item={item} mobile />)}
            </nav>
            <div className="border-t pt-3 space-y-1" style={{ borderColor: 'rgba(191,201,209,0.12)' }}>
              <UserBadge />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-400 hover:bg-red-500/10 transition-all"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      <main className={`transition-all duration-300 ease-in-out ${collapsed ? 'md:ml-[72px]' : 'md:ml-64'} mt-14 md:mt-0`}>
        {/* Top bar */}
        <div className="sticky top-0 z-20 h-16 bg-white border-b flex items-center justify-between px-6"
          style={{ borderColor: '#BFC9D1' }}>
          <p className="text-xs" style={{ color: '#BFC9D1' }}>{today}</p>
          <div className="flex items-center gap-3">
            <span
              className="px-3 py-1 rounded-full text-xs font-semibold capitalize text-white"
              style={{ backgroundColor: '#25343F' }}
            >
              {currentUser?.role}
            </span>
            <button
              onClick={() => setProfileOpen(true)}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <span className="text-sm font-semibold" style={{ color: '#25343F' }}>
                {currentUser?.name || currentUser?.username}
              </span>
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                style={{ background: 'linear-gradient(135deg,#FF9B51,#e07a33)' }}
              >
                {currentUser?.name ? currentUser.name[0].toUpperCase() : '?'}
              </div>
            </button>
          </div>
        </div>

        {/* Page */}
        <div className="p-6">{children}</div>
      </main>

      {/* ── Profile Modal ── */}
      {profileOpen && (
        <ProfileModal
          user={currentUser}
          onClose={() => setProfileOpen(false)}
          onUpdated={(u) => { handleProfileUpdate(u); setProfileOpen(false); }}
        />
      )}
    </div>
  );
};

export default AdminLayout;
